"use client";
import DebateAvatar from "@/components/common/DebateAvatar";
import { useD3Fetch } from "@/hook/useD3Fetch";
import { useSession } from "@/hook/useSession";
import { useCommentsStore } from "@/stores/commentsStore";
import { IUsers } from "@/types";
import { Comment } from "@/types/CommentsTypes";
import { formatRelativeTime } from "@/utils/formatUtil";
import { safeT } from "@/utils/safeT";
import * as Icons from "@mui/icons-material";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import DeleteToggleButton from "../common/DeleteToggleButton";
import GoogleTranslateShowMoreText from "../common/GoogleTranslateShowMoreText";
import LikeDislikeButton from "../common/LikeDislikeButton";
import PinToggleButton from "../common/PinToggleButton";

interface CommentItemProps {
  comment: Comment;
  lang: string;
  depth?: number;
  ref?: React.Ref<HTMLDivElement>;
  debateUser: IUsers;
}

const CommentItem = ({ comment: item, lang, depth = 0, debateUser, ref }: CommentItemProps) => {
  const { openReplies, toggleReplies, setComment, setUser, setActionType, setCommentInput, focusInput } =
    useCommentsStore();
  const hasReplies = item.comment.replies && item.comment.replies.length > 0;
  const isOpen = openReplies[item.comment.id];
  const theme = useTheme();
  const { session } = useSession();
  const t = useTranslations();
  const d3Fetch = useD3Fetch();
  const locale = useLocale();

  const handleCommentLike = (lang: string, id: string, state: { isLiked: boolean; isDisliked: boolean }) => {
    d3Fetch.post("/comments/like", { lang, commentId: id, ...state });
  };

  const handleCommentDislike = (lang: string, id: string, state: { isLiked: boolean; isDisliked: boolean }) => {
    d3Fetch.post("/comments/dislike", { lang, commentId: id, ...state });
  };

  function handleEditComment(): void {
    setActionType(t, "edit");
    setComment(item.comment);
    setCommentInput(item.comment.content);
    focusInput();
  }

  function handleReplyComment(): void {
    setUser(item.user);
    setComment(item.comment);

    if (depth > 0) {
      setActionType(t, "mention");
      setCommentInput(`@${item.user.nickname}`);
    } else {
      setActionType(t, "reply");
      setCommentInput("");
    }

    focusInput();
  }

  /**
   * 댓글 삭제
   * @param commentId
   * @param lang
   * @param del
   */
  const toggleDelete = (commentId: string, lang: string, del: boolean) => {
    if (del) {
      d3Fetch.del(`/comments/delete/${lang}/${item.comment.debateId}/${commentId}`);
    } else {
      d3Fetch.patch(`/comments/delete/${lang}/${item.comment.debateId}/${commentId}`, null);
    }
  };

  const togglePin = (commentId: string, lang: string, newPin: boolean) => {
    if (newPin) {
      d3Fetch.patch(`/comments/pin/${lang}/${item.comment.debateId}/${commentId}`, null);
    } else {
      d3Fetch.patch(`/comments/unpin/${lang}/${item.comment.debateId}/${commentId}`, null);
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mb: depth === 0 ? 2 : 1,
        position: "relative",
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <DebateAvatar name={item.user.nickname || ""} src={item.user.picture} size={24} />
      </Box>
      <Box flexGrow={1} ml={1 + depth * 1}>
        {/* 1단 Box: 아바타부터 삭제 버튼까지 */}
        <Box sx={{ position: "relative" }}>
          <Stack spacing={0.5}>
            {item.comment.isPinned ? (
              <Typography variant="caption" color="text.secondary" alignItems="center">
                <Icons.PushPinOutlined
                  sx={{
                    fontSize: "13px",
                    color: "text.secondary",
                    verticalAlign: "text-bottom", // 또는 "middle"
                  }}
                />
                {safeT(t, "ui.label.pinnedBy", {
                  defaultMessage: ` @${debateUser.nickname}님이 고정함`,
                  values: { name: ` @${debateUser.nickname}` },
                })}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.3} alignItems="center" flexWrap="wrap">
              {item.comment.isPinned ? (
                <Box
                  sx={{
                    backgroundColor: "background.paper",
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "9999px",
                    fontSize: "12px",
                    fontWeight: 500,
                    display: "inline-block",
                  }}
                >
                  {item.user.nickname}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {item.user.nickname}
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary">
                • {formatRelativeTime(item.comment.updatedAt, locale)}
              </Typography>

              {item.comment.createdAt !== item.comment.updatedAt && (
                <Typography variant="caption" color="text.secondary">
                  (
                  {safeT(t, "ui.label.edited", {
                    defaultMessage: "수정됨",
                  })}
                  )
                </Typography>
              )}

              {item.comment.stance && (
                <Icons.Favorite
                  sx={{
                    fontSize: "12px",
                    color: item.comment.stance === "pros" ? "#64BF55" : "#F44336",
                    pt: "2px",
                  }}
                />
              )}
            </Stack>

            <GoogleTranslateShowMoreText
              maxLines={4}
              sourceLang={item.comment.commentLang ?? lang}
              targetLang={locale}
              expanded={false}
              text={item.comment.content ?? ""}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <LikeDislikeButton
                lang={item.comment.lang}
                id={item.comment.id}
                initialLikeCount={item.comment.likeCount}
                initialDislikeCount={item.comment.dislikeCount}
                fontSize={18}
                initialLiked={item.like?.isLiked}
                initialDisliked={item.like?.isDisliked}
                onDislike={(lang, id, state) => {
                  //console.log("onDislike", lang, id, state);
                  handleCommentDislike(lang, id, state);
                }}
                onLike={(lang, id, state) => {
                  //console.log("onLike", lang, id, state);
                  handleCommentLike(lang, id, state);
                }}
              />
              {session && session.user?.id ? (
                <IconButton size="small" aria-label="add comment" onClick={handleReplyComment}>
                  <Icons.CommentOutlined sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                </IconButton>
              ) : null}
              {item.comment.userId === session?.user?.id ? (
                <>
                  <IconButton size="small" aria-label="edit comment" onClick={handleEditComment}>
                    <Icons.EditNote sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                  </IconButton>
                  <DeleteToggleButton id={item.comment.id} fontSize={18} lang={lang} onToggle={toggleDelete} />
                </>
              ) : null}
              {depth === 0 && debateUser.id === session?.user?.id ? (
                <PinToggleButton
                  id={item.comment.id}
                  fontSize={18}
                  initialPin={item.comment.isPinned ?? false}
                  lang={lang}
                  onToggle={togglePin}
                />
              ) : null}
              {item.debateCreatorLiked ? (
                <DebateAvatar name={debateUser.nickname || ""} src={debateUser.picture} size={18} showHeart />
              ) : null}
            </Stack>
          </Stack>
          {/* 수직선: 답글이 있으면 표시 */}
          {item.comment.replies && item.comment.replies.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: "-20px",
                top: "4px",
                width: "2px",
                height: "100%", // 1단 Box의 높이에서 아바타 bottom까지 빼기
                backgroundColor: theme.palette.divider,
              }}
            />
          )}
        </Box>

        {/* 2단 Box: 답글 (n개) 버튼 */}
        {item.comment.replies && item.comment.replies.length > 0 && (
          <Box sx={{ position: "relative", mt: 0.5 }}>
            {/* todo L 모양 선: 아바타 중심에서 하단까지 수직선, 답글 버튼까지 수평선 */}
            <Box
              sx={{
                position: "absolute",
                left: "-20px",
                top: "0", // 아바타 상단에서 시작
                width: "24px",
                height: "50%", // 아바타 높이(24px) + 2단 Box 높이
                borderLeft: `2px solid ${theme.palette.divider}`, // 수직선
                borderBottom: `2px solid ${theme.palette.divider}`, // 수평선
                borderRadius: "0 0 0 10px", // L 모양의 하단 좌측만 둥글게
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                aria-label="reply"
                onClick={() => toggleReplies(item.comment.id)}
                sx={{
                  borderRadius: 2, // 16px 정도의 둥근 직사각형
                  px: 1.2, // 좌우 패딩
                  py: 0.5, // 상하 패딩
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <Typography variant="caption" sx={{ ml: 0.5, color: theme.palette.text.secondary, fontWeight: 600 }}>
                  {safeT(t, "ui.label.replyCount", {
                    defaultMessage: `${item.comment.replies.length}개`,
                    values: { count: `${item.comment.replies.length}` },
                  })}
                </Typography>
                {openReplies[item.comment.id] ? (
                  <Icons.KeyboardArrowDownOutlined sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                ) : (
                  <Icons.KeyboardArrowRightOutlined sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                )}
              </IconButton>
            </Box>
          </Box>
        )}

        {hasReplies && isOpen && (
          <Box sx={{ mt: 1 }}>
            {item.comment.replies!.map((reply) => (
              <CommentItem
                debateUser={debateUser}
                key={reply.comment.id}
                comment={reply}
                lang={lang}
                depth={depth + 1}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CommentItem;
